interface HubSpotFormHandle {
  getInstanceId: () => string
}

declare global {
  interface Window {
    HubSpotFormsV4?: {
      getForms: () => HubSpotFormHandle[]
      getFormFromEvent: (event: Event) => HubSpotFormHandle | undefined
    }
  }
}

const READY_EVENT = 'hs-form-event:on-ready'

const OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['for'],
}

function deduplicateWrappingLabels(form: HTMLElement): void {
  form.querySelectorAll<HTMLLabelElement>('label[for]').forEach((label) => {
    const forId = label.getAttribute('for')
    const controls = label.querySelectorAll<HTMLElement>('input, select, textarea')
    if (forId && Array.from(controls).some(control => control.id === forId))
      label.removeAttribute('for')
  })
}
function groupQuestionsAsFieldsets(form: HTMLElement): void {
  const groups = form.querySelectorAll<HTMLElement>(
    '.hsfc-RadioFieldGroup, .hsfc-CheckboxFieldGroup',
  )

  groups.forEach((group) => {
    const label = group.querySelector<HTMLLabelElement>(
      ':scope > label.hsfc-FieldLabel',
    )

    if (group.querySelector(':scope > fieldset'))
      return

    if (
      !label
      || label.hasAttribute('for')
      || label.querySelector('input, select, textarea')
    ) {
      return
    }

    const fieldset = document.createElement('fieldset')
    const legend = document.createElement('legend')

    Array.from(label.attributes).forEach(({ name, value }) => {
      legend.setAttribute(name, value)
    })

    legend.append(...Array.from(label.childNodes))

    fieldset.append(...Array.from(group.childNodes))
    label.replaceWith(legend)
    group.append(fieldset)
  })
}

/**
 * Apply all fixes to every HubSpot form below `root`, naming each one
 * `accessibleName` when given. Idempotent.
 *
 * This intentionally mutates the rendered HubSpot DOM. It must therefore
 * only be called after HubSpot has completed its React render/hydration.
 */
export function applyHubSpotFormA11yFixes(root: ParentNode, accessibleName?: string): void {
  const name = accessibleName?.trim()

  root.querySelectorAll<HTMLElement>('form').forEach((form) => {
    // The embed hardcodes aria-label="HubSpot Form", so two forms on one page are
    // the same landmark until this replaces it.
    if (name)
      form.setAttribute('aria-label', name)

    deduplicateWrappingLabels(form)
    groupQuestionsAsFieldsets(form)
  })
}

/**
 * Resolve the actual HubSpot `.hs-form-html` mount element.
 *
 * Some callers pass the mount element's ID directly. Footer/sidebar forms
 * instead pass a wrapper ID, and useHubSpotFormHtml creates `.hs-form-html`
 * inside that wrapper. HubSpot then assigns the mount element its own UUID.
 */
function resolveFormContainer(
  containerId: string,
  instanceId?: string,
): HTMLElement | undefined {
  const container = document.getElementById(containerId)

  if (!container)
    return undefined

  // OneEightyFormBlock/error.vue: the supplied ID is the actual mount ID.
  if (container.classList.contains('hs-form-html')) {
    if (!instanceId || container.id === instanceId)
      return container

    return undefined
  }

  // Footer/sidebar: the supplied ID is a wrapper around the actual mount.
  const form = container.querySelector<HTMLElement>('.hs-form-html')

  if (!form)
    return undefined

  // When an instance ID is available, make sure this is the exact
  // HubSpot instance that fired the event.
  if (instanceId && form.id !== instanceId)
    return undefined

  return form
}

/**
 * Apply accessibility fixes to the HubSpot form identified by `containerId`.
 *
 * HubSpot's developer embed renders the form using React. The form must not
 * be modified while React is hydrating it because structural DOM changes can
 * cause React hydration errors (#418/#422).
 *
 * The V4 `hs-form-event:on-ready` event provides the appropriate lifecycle
 * boundary. The getForms() lookup handles the case where the form finished
 * rendering before this listener was attached.
 *
 * `accessibleName` becomes the form's `aria-label`; pass one wherever a page can
 * hold more than one form, or they collide as identical landmarks.
 */
export function useHubSpotFormA11y(containerId: string, accessibleName?: string): void {
  let observer: MutationObserver | undefined
  let container: HTMLElement | undefined | null

  function applyFixes(): void {
    if (!container)
      return

    observer?.disconnect()

    try {
      applyHubSpotFormA11yFixes(container, accessibleName)
    }
    finally {
      observer?.observe(container, OBSERVE_OPTIONS)
    }
  }

  function onMutations(mutations: MutationRecord[]): void {
    if (!mutations.some(mutation =>
      mutation.type === 'childList' || mutation.attributeName === 'for',
    )) {
      return
    }

    applyFixes()
  }

  function startObserver(): void {
    if (!container || observer)
      return

    observer = new MutationObserver((mutations) => {
      onMutations(mutations)
    })

    applyHubSpotFormA11yFixes(container, accessibleName)

    observer.observe(container, OBSERVE_OPTIONS)
  }

  function onReady(event: Event): void {
    const { instanceId } = (event as CustomEvent<{ instanceId?: string }>).detail ?? {}

    if (!instanceId)
      return

    const resolved = resolveFormContainer(containerId, instanceId)

    if (!resolved)
      return

    container = resolved
    startObserver()
  }

  onMounted(() => {
    window.addEventListener(READY_EVENT, onReady)

    /*
     * The form may already have completed before our listener was attached.
     *
     * Find the V4 instance first, then resolve the actual mount element using
     * its instance ID. This works for both explicit and generated IDs.
     */
    const existingForm = window.HubSpotFormsV4
      ?.getForms()
      .find((form) => {
        const resolved = resolveFormContainer(
          containerId,
          form.getInstanceId(),
        )

        if (!resolved)
          return false

        container = resolved
        return true
      })

    if (existingForm)
      startObserver()
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = undefined
    container = undefined
    window.removeEventListener(READY_EVENT, onReady)
  })
}
